// @flow

import React from 'react';
import get from 'lodash.get';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import { Input } from 'dibs-elements/exports/Input';
import { Button } from 'dibs-elements/exports/Button';
import { Radio } from 'dibs-elements/exports/Radio';
import { Spinner } from 'dibs-elements/exports/Spinner';

import { commit } from '../mutations/CreateProjectMutation';
import { isValidDateInput, dateInputToTimestamp } from '../helpers/date';
import { postProjectToFA } from '../faPoster';

import type { Environment } from 'react-relay';
import type { IntlShape } from 'react-intl';
import type { FormStatus, Project } from '../types.flow';

import styles from './styles/CreateProject.scss';

//exported for test
export const SOURCING_REQUESTED = 'SOURCING_REQUESTED';

const messages = defineMessages({
    nameCollisionError: {
        id: 'dc.ProjectForms.CreateProject.nameInputError',
        defaultMessage: 'You aleady have a project called "{projectName}"',
    },
    namePlaceholder: {
        id: 'dc.ProjectForms.CreateProject.namePlaceholder',
        defaultMessage: 'Example Project',
    },
    installDatePlaceholder: {
        id: 'dc.ProjectForms.CreateProject.installDatePlaceholder',
        defaultMessage: 'MM/DD/YYYY',
    },
});

type OnSuccessPayload = {|
    project: mixed,
    sourcingAssistanceRequested: boolean,
|};

export type CreateProjectProps = {|
    firmId: ?string,
    onSuccess?: (payload: OnSuccessPayload) => mixed,
    relayEnvironment: Environment,
    source: string,
    userId: string,
|};
type CreateProjectComponentProps = {|
    ...CreateProjectProps,
    intl: IntlShape,
|};

type CreateProjectState = {|
    formStatus: FormStatus,
    nameCollisions: Array<string>,
    project: Project,
    sourcingAssistanceRequested: boolean | null,
|};

export class CreateProject extends React.Component<
    CreateProjectComponentProps,
    CreateProjectState
> {
    constructor(props: CreateProjectComponentProps) {
        super(props);

        this.state = {
            formStatus: 'DEFAULT',
            nameCollisions: [],
            project: {},
            sourcingAssistanceRequested: null,
        };
    }

    canSubmit = () => {
        const { project, sourcingAssistanceRequested } = this.state;
        return (
            !!project.name &&
            typeof sourcingAssistanceRequested === 'boolean' &&
            !this.hasInputNameCollision()
        );
    };

    handleMutationError = (error: mixed) => {
        const name = this.state.project.name;
        const errors = get(error, 'source.errors') || [];
        const isProjectNameCollision = !!errors.find(err => get(err, 'requestInfo.status') === 409);

        if (name && isProjectNameCollision) {
            const nameCollisions = this.state.nameCollisions;
            this.setState({ nameCollisions: [...nameCollisions, name], formStatus: 'DEFAULT' });
        } else {
            this.setState({ formStatus: 'ERROR' });
        }
    };

    handleMutationSuccess = (payload: mixed) => {
        const { firmId, onSuccess, source, userId } = this.props;
        const { sourcingAssistanceRequested } = this.state;

        const project = get(payload, 'createProject.project');

        postProjectToFA({
            action: 'CREATE',
            firmId,
            project,
            source,
            userId,
        });

        if (typeof onSuccess === 'function') {
            onSuccess({
                project,
                sourcingAssistanceRequested: !!sourcingAssistanceRequested,
            });
        }
    };

    hasInputNameCollision = () => {
        const { project, nameCollisions } = this.state;
        const { name } = project;
        return !!name && nameCollisions.indexOf(name) > -1;
    };

    setSourcingOption = (sourcingOptionChosen: ?(string | number)) => {
        const sourcingRequested = sourcingOptionChosen === SOURCING_REQUESTED;
        this.setState({
            sourcingAssistanceRequested: sourcingRequested,
        });
    };

    submit = (e: SyntheticInputEvent<*>) => {
        e.preventDefault();
        const { relayEnvironment, userId } = this.props;
        const { formStatus, project } = this.state;

        if (formStatus === 'LOADING') {
            return;
        }

        const { installDate, name } = project;
        const formattedInstallDate = dateInputToTimestamp(installDate);

        this.setState({ formStatus: 'LOADING' });

        commit(relayEnvironment, {
            name,
            installDate: formattedInstallDate,
            onCompleted: this.handleMutationSuccess,
            onError: this.handleMutationError,
            userId,
        });
    };

    updateProject = (field: string) => {
        const oldProjectState = this.state.project;

        return (e: SyntheticInputEvent<*>) => {
            const newProjectSate = {
                ...oldProjectState,
                [field]: e.target.value,
            };
            this.setState({
                project: newProjectSate,
            });
        };
    };

    render() {
        const { intl } = this.props;
        const { formStatus, project, sourcingAssistanceRequested } = this.state;
        const { installDate, name } = project;
        const validInstallDate = isValidDateInput(installDate);
        const showInstallDateError = !!installDate && !validInstallDate;
        const nameInputError = this.hasInputNameCollision()
            ? intl.formatMessage(messages.nameCollisionError, { projectName: name })
            : '';

        return (
            <form onSubmit={this.submit}>
                <div className={styles.formSection}>
                    <div className={styles.formSectionPrompt}>
                        <FormattedMessage
                            id="dc.ProjectForms.CreateProject.name"
                            defaultMessage="Project Name"
                        />
                    </div>
                    <Input
                        dataTn="project-name-input"
                        errorMessage={nameInputError}
                        onChange={this.updateProject('name')}
                        maxLength={31}
                        placeholder={intl.formatMessage(messages.namePlaceholder)}
                        value={name || ''}
                    />
                </div>
                <div className={styles.formSection}>
                    <div className={styles.formSectionPrompt}>
                        <FormattedMessage
                            id="dc.ProjectForms.CreateProject.installDate"
                            defaultMessage="When will this project be installed"
                        />
                        <span className={styles.formSectionPromptSecondary}>
                            <FormattedMessage
                                id="dc.ProjectForms.CreateProject.isOptional"
                                defaultMessage="(is optional)"
                            />
                        </span>
                    </div>
                    <Input
                        dataTn="project-install-date-input"
                        hasValidText={validInstallDate}
                        errorMessage={showInstallDateError ? ' ' : null}
                        onChange={this.updateProject('installDate')}
                        placeholder={intl.formatMessage(messages.installDatePlaceholder)}
                        value={installDate || ''}
                    />
                </div>
                <div className={styles.formSection}>
                    <div className={styles.formSectionPrompt}>
                        <FormattedMessage
                            id="dc.ProjectForms.CreateProject.soucingAssistance"
                            defaultMessage="Do you need 1stdibs sourcing assistance?"
                        />
                    </div>
                    <Radio
                        checked={sourcingAssistanceRequested === true}
                        dataTn="sourcing-requested-yes"
                        label="Yes"
                        labelClass={styles.checkboxLabelClass}
                        name="sourcing-requested-yes"
                        onChange={this.setSourcingOption}
                        value={SOURCING_REQUESTED}
                        wrapperClass={styles.radioWrapperClass}
                    />
                    <Radio
                        checked={sourcingAssistanceRequested === false}
                        dataTn="sourcing-requested-no"
                        label="No"
                        labelClass={styles.checkboxLabelClass}
                        name="sourcing-requested-no"
                        onChange={this.setSourcingOption}
                        value={''}
                        wrapperClass={styles.radioWrapperClass}
                    />
                </div>
                <Button
                    className={styles.submitButton}
                    dataTn="create-new-project-submit-button"
                    disabled={!this.canSubmit()}
                >
                    {formStatus === 'LOADING' ? (
                        <Spinner containerClass={styles.spinnerContainer} />
                    ) : (
                        <FormattedMessage
                            id="dc.ProjectForms.CreateProject.submitCta"
                            defaultMessage="Done"
                        />
                    )}
                </Button>
                {formStatus === 'ERROR' && (
                    <div className={styles.errorMessage}>
                        <FormattedMessage
                            id="dc.ProjectForms.CreateProject.errorMessage"
                            defaultMessage="An error occurred. Form submission failed."
                        />
                    </div>
                )}
            </form>
        );
    }
}

export default injectIntl(CreateProject);
